function toWellFormedFallback(input: string): string {
  function isSurrogate(cc: number) {
    return (cc & 0xf800) === 0xd800
  }
  function isSurrogateTail(cc: number) {
    return (cc & 0x400) !== 0
  }
  let result = ''
  for (let i = 0; i < input.length; ++i) {
    const cc1 = input.charCodeAt(i)
    if (!isSurrogate(cc1)) {
      result += input.charAt(i)
      continue
    }
    if (!isSurrogateTail(cc1) && i + 1 < input.length) {
      const cc2 = input.charCodeAt(i + 1)
      if (isSurrogate(cc2) && isSurrogateTail(cc2)) {
        result += input.charAt(i)
        result += input.charAt(i + 1)
        ++i
        continue
      }
    }
    result += '\uFFFD'
  }
  return result
}

function toWellFormed(input: string): string {
  const maybe = input as string & { toWellFormed?: () => string }
  if (typeof maybe.toWellFormed === 'function') {
    return maybe.toWellFormed()
  }
  return toWellFormedFallback(input)
}

export class UnsafeUrlError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UnsafeUrlError'
  }
}

const DOT_SEGMENT = /(?:^|\/)(?:\.|%2e){1,2}(?:\/|$)/i

function hasDotSegment(candidate: string): boolean {
  // only looks for dot segments in the path, read as browsers do: no tab or newline, `\` is `/`
  const path = candidate.replace(/[\t\n\r]/g, '').replace(/[?#][\s\S]*$/, '')
  return DOT_SEGMENT.test(path.replace(/\\/g, '/'))
}

export type SafeURL = string & {
  __brand: 'URL constructed safely to avoid path traversal bugs, e.g. through xr.url'
}

export type SafeURLComponent = { safeUrlComponent: string }

const SAFE_URL_COMPONENT = Symbol.for('@figma/safe-url/SafeURLComponent')

function isSafeURLComponent(foo: unknown): foo is SafeURLComponent {
  return (
    typeof foo === 'object' &&
    foo !== null &&
    (foo as { [SAFE_URL_COMPONENT]?: unknown })[SAFE_URL_COMPONENT] === true &&
    typeof (foo as { safeUrlComponent?: unknown }).safeUrlComponent === 'string'
  )
}

/**
 * This is a tag function for safely templating URLs, taking care of URL-encoding to prevent URL injection bugs:
 * url`/org/{orgId}/files` returns '/org/123/files' when orgId = '123',
 * but '/org/..%2F..%2Fadmin%3Fa%3D/files' when orgId = '../../admin?a='.
 * Throws `UnsafeUrlError` when the result contains a whole dot segment.
 */
export function url(
  strings: TemplateStringsArray,
  ...parts: (string | number | string[] | boolean | null | undefined | SafeURLComponent)[]
): SafeURL {
  let result = ''

  strings.forEach((string, index) => {
    result += string

    if (index < strings.length - 1) {
      if (isSafeURLComponent(parts[index])) {
        // component has already been pre-escaped, do not double-escape
        result += parts[index].safeUrlComponent
      } else {
        // wrap parts[index] in `${}` to match how it would get stringified without this function
        const part = String(parts[index])
        // encodeURIComponent throws on UTF16 lone surrogates, so remove them
        result += encodeURIComponent(toWellFormed(part))
      }
    }
  })

  // Rejects a whole '.' or '..' path segment, including its variants like '%2e'.
  if (hasDotSegment(result)) {
    throw new UnsafeUrlError('Invalid Path.')
  }

  return result as SafeURL
}

/**
 * This is an escape hatch that can be used to embed unescaped strings inside url,
 * so url`${dangerousUnescapedUrlComponent('https://example.com')}/${'foo/bar'}`
 * will return 'https://example.com/foo%2Fbar'.
 * This should only be used when a string is hardcoded or otherwise known to be safe
 * and free from user input, e.g. when templating a known cross-origin domain into a URL.
 */
export function dangerousUnescapedUrlComponent(component: string): SafeURLComponent {
  return { safeUrlComponent: component, [SAFE_URL_COMPONENT]: true } as SafeURLComponent
}
